// frontend/src/pages/TankShotsComparePage.tsx
// Compare two tank shots (/livestock/tank-shots/compare?tank=&before=&after=,
// design 7): the selected tank's shots in the shared compare page
// (features/livestock/ComparePageBody.tsx; livestock compare design 8.2).
// `?tank=` and the selected tank are kept together by useTankParam: while the
// URL's tank is being selected the page waits, and a switch in the tank picker
// shows the new tank's First vs Latest.

import { useMemo } from 'react';
import { Container } from '@mantine/core';
import { useSearchParams } from 'react-router-dom';
import { useTank } from '@/contexts/TankContext';
import { NoTankSelected } from '@/components/common/NoTankSelected';
import { ComparePageBody } from '@/features/livestock/ComparePageBody';
import type { PhotoSubject } from '@/features/livestock/photoSubject';
import { useTankParam } from '@/features/livestock/useTankParam';

export function TankShotsComparePage(): JSX.Element {
  const { selectedTank } = useTank();
  const switchingTank = useTankParam();
  const [params] = useSearchParams();
  const tankId = switchingTank ? undefined : selectedTank?.id;
  // One object per tank, so a re-render never hands the body a new subject
  const subject = useMemo<PhotoSubject | undefined>(
    () => (tankId ? { kind: 'tank', tankId } : undefined),
    [tankId]
  );

  if (!selectedTank) {
    return (
      <Container size="lg" py="xl">
        <NoTankSelected purpose="to compare its tank shots" />
      </Container>
    );
  }

  return (
    <ComparePageBody
      subject={subject}
      backMode="link"
      subtitle={selectedTank.name}
      backTo={`/livestock/tank-shots?tank=${encodeURIComponent(selectedTank.id)}`}
      backLabel="Back to Tank Shots"
      switching={switchingTank}
      switchKey={params.get('tank')}
      emptyDescription="Take a tank shot from the same spot each month to see how your reef grows in"
      shareName={selectedTank.name}
    />
  );
}
